import { Box, LinearProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getTrans } from 'modules/i18n';
import { useProgressCalculation } from '../hooks/useProgressCalculation';

interface ProgressBarProps {
  showPercentage?: boolean;
  showSteps?: boolean;
  variant?: 'determinate' | 'indeterminate';
  size?: 'small' | 'medium' | 'large';
}

const getProgressColor = (totalProgress: number) => {
  return totalProgress >= 100 ? 'success' : 'primary';
};

const getHeight = (size: string) => {
  switch (size) {
    case 'small':
      return 4;
    case 'large':
      return 12;
    default:
      return 8;
  }
};

export const ProgressBar = ({
  showPercentage = true,
  showSteps = true,
  variant = 'determinate',
  size = 'medium'
}: ProgressBarProps) => {
  const theme = useTheme();
  const { totalProgress, completedSteps, totalSteps } =
    useProgressCalculation();

  // Themed gradient + accent: green when complete, brand blue otherwise.
  const accent =
    totalProgress >= 100 ? theme.palette.success : theme.palette.primary;
  const progressGradient = `linear-gradient(90deg, ${accent.main} 0%, ${accent.light} 100%)`;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Progress Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: showSteps ? 2 : 0
        }}
      >
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant={variant}
            value={variant === 'determinate' ? totalProgress : undefined}
            color={getProgressColor(totalProgress)}
            sx={{
              height: getHeight(size),
              borderRadius: getHeight(size) / 2,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: getHeight(size) / 2,
                background: progressGradient
              }
            }}
          />
        </Box>
        {showPercentage && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              minWidth: '40px',
              textAlign: 'right',
              fontWeight: 'bold',
              color: accent.main
            }}
          >
            {completedSteps}/{totalSteps}
          </Typography>
        )}
      </Box>

      {/* Progress Summary */}
      {totalProgress >= 100 && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
          <Typography
            variant="body2"
            color="success.main"
            sx={{ fontWeight: 'bold' }}
          >
            {getTrans('cvBuilderPage.progress.complete')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
